// src/components/admin/TabGroup.jsx
import { useEffect, useMemo, useRef, useState } from "react";

export default function TabGroup({ tabs, activeTab, setActiveTab }) {
  // id estable para aria
  const toId = (name) =>
    `tab-${String(name).toLowerCase().replace(/\s+/g, "-").replace(/[^a-z0-9\-]/g, "")}`;

  const ids = useMemo(() => tabs.map((t) => toId(t.name)), [tabs]);
  const activeIndex = Math.max(0, tabs.findIndex((t) => t.name === activeTab?.name));

  // refs
  const containerRef = useRef(null);
  const refs = useRef([]);
  const [indicator, setIndicator] = useState({ left: 0, width: 0, top: 0 });

  // Calcula posición del indicador bajo el tab activo
  const recalcIndicator = () => {
    const c = containerRef.current;
    const btn = refs.current[activeIndex];
    if (!c || !btn) return;
    const cRect = c.getBoundingClientRect();
    const bRect = btn.getBoundingClientRect();
    const left = bRect.left - cRect.left;
    const top = btn.offsetTop + btn.offsetHeight - 2;
    setIndicator({ left, width: bRect.width, top });
  };

  // Enfoca y centra el activo en móvil + recalcula indicador
  useEffect(() => {
    const btn = refs.current[activeIndex];
    const c = containerRef.current;
    if (btn && c) {
      // tabs en varias filas: no hay scroll horizontal que centrar
    }
    // Opcional: no robamos foco si el usuario ya está navegando con teclado
    if (btn && document.activeElement?.getAttribute("role") !== "tab") {
      // btn.focus(); // descomenta si quieres auto-focus
    }
    recalcIndicator();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeIndex, tabs.length]);

  // Recalcular al redimensionar
  useEffect(() => {
    let rAF;
    const onResize = () => {
      cancelAnimationFrame(rAF);
      rAF = requestAnimationFrame(recalcIndicator);
    };
    window.addEventListener("resize", onResize);
    return () => {
      window.removeEventListener("resize", onResize);
      cancelAnimationFrame(rAF);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onKeyDown = (e, index) => {
    const max = tabs.length - 1;
    let next = index;

    switch (e.key) {
      case "ArrowRight":
        next = index === max ? 0 : index + 1;
        break;
      case "ArrowLeft":
        next = index === 0 ? max : index - 1;
        break;
      case "Home":
        next = 0;
        break;
      case "End":
        next = max;
        break;
      default:
        return;
    }

    e.preventDefault();
    const tab = tabs[next];
    setActiveTab(tab);
    refs.current[next]?.focus();
  };

  return (
    <div
      className="
        sticky top-0 z-30 -mx-6 px-6 pt-2 pb-3
        bg-[#F4F6F8]/70 backdrop-blur supports-[backdrop-filter]:backdrop-blur
      "
    >
      <div className="relative">
        <div
          ref={containerRef}
          role="tablist"
          aria-label="Secciones del panel de administración"
          className="
            relative
            flex flex-wrap justify-center gap-2 rounded-2xl border border-gray-200 bg-white/90 p-1 shadow-sm
          "
        >
          {/* Indicador inferior */}
          <div
            aria-hidden
            className="absolute h-0.5 bg-[#004B87] rounded-full transition-all duration-200 ease-in-out"
            style={{
              left: indicator.left + "px",
              width: indicator.width + "px",
              top: indicator.top + "px",
            }}
          />

          {tabs.map((t, i) => {
            const isActive = activeTab?.name === t.name;
            const id = ids[i];
            return (
              <button
                key={t.name}
                id={`${id}-tab`}
                ref={(el) => (refs.current[i] = el)}
                role="tab"
                aria-selected={isActive}
                aria-controls={`${id}-panel`}
                tabIndex={isActive ? 0 : -1}
                onClick={() => setActiveTab(t)}
                onKeyDown={(e) => onKeyDown(e, i)}
                className={[
                  // Botón base
                  "inline-flex items-center gap-2 rounded-xl px-4 py-2 text-sm font-medium transition-colors duration-200 ease-in-out outline-none",
                  // Estado visual
                  isActive
                    ? "bg-[#004B87]/[0.08] text-[#004B87]"
                    : "text-slate-500 hover:bg-[#004B87]/[0.03] hover:text-slate-700",
                  // Accesibilidad
                  "focus-visible:ring-2 focus-visible:ring-blue-300",
                  // Tocar confort en móvil
                  "min-h-[40px]",
                ].join(" ")}
                title={t.label}
              >
                <span aria-hidden className={isActive ? "opacity-100" : "opacity-70"}>
                  {t.icon ?? "●"}
                </span>
                <span className="whitespace-nowrap">{t.label}</span>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
